import type { RecordProxy, RecordSourceSelectorProxy } from 'relay-runtime';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { MenuItemReorderOperation } from '../../../utils/constants.tsx';
import { reorder } from '../../../utils/reorder-list.tsx';

/**
 * Time complexity: O(n)
 * Reorders the edges in a favorite connection based on a provided sorted list of projectId:
 * 1. Retrieves the favorite connection from the store
 * 2. Gets all edges from the connection
 * 3. Creates a map of projectId -> edge
 * 4. Reconstructs the edges array in the order specified by sortedProjectIds
 * 5. Updates the connection with the reordered edges
 *
 * @param store - The Relay store to update
 * @param favouriteConnectionId - The ID of the favorite connection to reorder
 * @param sortedProjectIds - Array of project IDs in the desired order
 */
export function optimisticUpdater(
	store: RecordSourceSelectorProxy,
	favouriteConnectionId: string,
	operation: MenuItemReorderOperation,
	projectIds: ProjectId[],
	targetProjectId: ProjectId,
) {
	if (!projectIds.length || projectIds.length <= 1) {
		return;
	}

	const favoriteConnection = store.get(favouriteConnectionId);
	if (!favoriteConnection) {
		return;
	}

	// Retrieve all edges from the connection
	const edges = favoriteConnection.getLinkedRecords('edges') ?? [];
	if (!edges.length || edges.length <= 1) {
		return;
	}

	const sortedProjectIds = reorder(operation, projectIds, targetProjectId);
	// Create a Set to check if sortedProjectIds has different ids from the Relay records
	const projectIdSet = new Set<ProjectId>(sortedProjectIds);
	// Create a Map to store projectId -> edge
	const projectIdToEdgeMap = new Map<ProjectId, RecordProxy>();

	for (const edge of edges) {
		const node = edge.getLinkedRecord('node');
		const projectId = String(node?.getValue('projectId'));

		if (projectId) {
			// Exit if the Relay record doesn't match the sortedProjectIds
			if (!projectIdSet.has(projectId)) {
				return;
			}

			projectIdToEdgeMap.set(projectId, edge);
		}
	}

	// Reconstruct edges array in the order specified by sortedProjectIds
	const sortedEdges = [];
	for (const projectId of sortedProjectIds) {
		const edge = projectIdToEdgeMap.get(projectId);
		if (edge) {
			sortedEdges.push(edge);
		}
	}

	// Update the connection with the newly ordered edges
	favoriteConnection.setLinkedRecords(sortedEdges, 'edges');
}
